import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../api";
import { Spinner } from "../components/Logo";
import { ProgressBar, LevelChip } from "../components/ui";
import { CheckCircle2, Lock, Circle, ArrowLeft, Target, PlayCircle } from "lucide-react";
import { mediaSrc } from "../api";

export default function PathDetail() {
  const { pathId } = useParams();
  const navigate = useNavigate();
  const [path, setPath] = useState(null);

  useEffect(() => {
    api.get(`/learning/paths/${pathId}`).then((r) => setPath(r.data.path)).catch(() => {});
  }, [pathId]);

  if (!path) return <Spinner />;

  const nextModule = path.modules.find((m) => !m.completed && !m.locked);

  return (
    <div data-testid="path-detail">
      <Link to="/app/learning" className="btn-ghost text-sm mb-4 -ml-2"><ArrowLeft size={16} /> Back to Learning</Link>

      <div className="card overflow-hidden mb-8">
        <div className="h-40 sm:h-52 relative">
          {path.cover_url && <img src={mediaSrc(path.cover_url)} alt={path.title} className="w-full h-full object-cover opacity-60" />}
          <div className="absolute inset-0 bg-gradient-to-t from-[#121C30] via-[#121C30]/40 to-transparent" />
          <div className="absolute bottom-0 p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-3">
              {path.category && <span className="chip bg-black/40 text-white">{path.category}</span>}
              <LevelChip level={path.level} />
            </div>
            <h1 className="font-heading text-2xl sm:text-4xl font-bold">{path.title}</h1>
          </div>
        </div>
        <div className="p-6 sm:p-8">
          <p className="text-slate-300 leading-relaxed max-w-3xl">{path.description}</p>
          {path.objectives && (
            <div className="mt-5 flex items-start gap-3 text-slate-300">
              <Target size={18} className="text-[#38bdf8] mt-0.5 shrink-0" />
              <p className="text-sm">{path.objectives}</p>
            </div>
          )}
          <div className="mt-6 flex flex-wrap items-center gap-6">
            <div className="flex-1 min-w-[220px]">
              <div className="flex justify-between text-sm text-slate-400 mb-1.5">
                <span>{path.completed_modules}/{path.total_modules} modules complete</span>
                <span className="text-white font-medium">{path.progress_pct}%</span>
              </div>
              <ProgressBar pct={path.progress_pct} />
            </div>
            {nextModule && (
              <button className="btn-primary" onClick={() => navigate(`/app/modules/${nextModule.id}`)} data-testid="continue-path-btn">
                <PlayCircle size={18} /> {path.completed_modules ? "Continue" : "Start"} path
              </button>
            )}
          </div>
        </div>
      </div>

      <h2 className="font-heading text-xl font-semibold mb-4">Roadmap</h2>
      <div className="space-y-3">
        {path.modules.map((m, i) => {
          const state = m.completed ? "done" : m.locked ? "locked" : "open";
          return (
            <div
              key={m.id}
              className={`card p-5 flex items-center gap-4 ${state === "locked" ? "opacity-60" : "card-hover cursor-pointer"}`}
              onClick={() => state !== "locked" && navigate(`/app/modules/${m.id}`)}
              data-testid={`module-row-${i}`}
            >
              <div className="shrink-0">
                {state === "done" ? <CheckCircle2 className="text-emerald-400" size={26} />
                  : state === "locked" ? <Lock className="text-slate-500" size={24} />
                  : <Circle className="text-[#F26522]" size={24} />}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 font-mono">#{i + 1}</span>
                  <h3 className="font-semibold">{m.title}</h3>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="chip bg-[#F26522]/10 text-[#F26522] text-xs">+{m.xp_reward} XP</div>
                {state === "locked" && <div className="text-xs text-slate-500 mt-1">Complete previous</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
